/**
 * FCL report UI tokens — aligned with Export/Import Shipment List shell.
 */

/** Full-width layout — matches BookingList / ImportBookingList (no max-w cap on zoom out). */
export const reportPage = "min-h-full w-full space-y-3 p-4";
/** Booking / detail report pages — slightly less top padding */
export const reportDetailPage = "min-h-full w-full space-y-2 px-4 pt-2 pb-4";
export const reportContainer = "w-full min-h-full space-y-3";
export const reportDetailContainer = "w-full min-h-full space-y-2";

/* Premium outer shell (Shipment List header card) */
export const reportShell =
    "rounded-2xl overflow-hidden " +
    "border border-gray-200 dark:border-gray-800 " +
    "bg-white dark:bg-gray-900 " +
    "shadow-sm dark:shadow-gray-900/40";

export const reportShellTopBar =
    "px-4 sm:px-5 py-4 " +
    "flex flex-wrap items-center justify-between gap-3 " +
    "border-b border-gray-100 dark:border-gray-800";

export const reportShellTopBarCompact =
    "px-4 sm:px-5 py-3 " +
    "flex flex-wrap items-center justify-between gap-3 " +
    "border-b border-gray-100 dark:border-gray-800";

export const reportShellBody = "px-4 sm:px-5 pb-4 sm:pb-5 space-y-3";

/* Cards & panels */
export const reportCard =
    "rounded-2xl border border-gray-200 dark:border-gray-800 " +
    "bg-white dark:bg-gray-900 shadow-sm dark:shadow-gray-900/30 " +
    "transition-all duration-200 ease-out";
export const reportCardPadding = "p-4 sm:p-5";
export const reportCardHover =
    "hover:border-blue-300/80 dark:hover:border-blue-500/40 " +
    "hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.01]";

export const reportMutedText = "text-gray-500 dark:text-gray-400";
export const reportBodyText = "text-gray-700 dark:text-gray-300";
export const reportHeading = "text-gray-900 dark:text-white";

/* Header typography */
export const reportHeaderBreadcrumb =
    "text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] " +
    "text-gray-500 dark:text-gray-400";

export const reportHeaderTitle =
    "truncate text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight " +
    "text-gray-900 dark:text-white";

export const reportHeaderIconWrap =
    "shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl " +
    "bg-blue-50 dark:bg-blue-900/40 " +
    "ring-1 ring-blue-100 dark:ring-blue-800 " +
    "flex items-center justify-center";

export const reportHeaderBackBtn =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors " +
    "border-gray-200 bg-white text-gray-600 " +
    "hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 " +
    "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 " +
    "dark:hover:bg-gray-700/70 dark:hover:border-gray-600 dark:hover:text-white";

export const reportHeaderIconBtn =
    "flex h-9 w-9 items-center justify-center rounded-lg border transition-colors " +
    "border-gray-200 bg-white text-gray-600 " +
    "hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 " +
    "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 " +
    "dark:hover:bg-gray-700/70 dark:hover:border-gray-600 dark:hover:text-white";

export const reportHeaderPrintBtn =
    "inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 sm:px-3.5 text-[12.5px] font-semibold transition-colors " +
    "border-gray-200 bg-white text-gray-700 " +
    "hover:bg-gray-50 hover:border-gray-300 " +
    "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 " +
    "dark:hover:bg-gray-700/70 dark:hover:border-gray-600";

export const reportHeaderExportBtn =
    "inline-flex h-9 items-center gap-1.5 rounded-lg px-3 sm:px-3.5 text-[12.5px] font-semibold text-white transition-all " +
    "bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 " +
    "shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)] dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)] " +
    "hover:shadow-[0_6px_20px_-4px_rgba(37,99,235,0.55)] disabled:opacity-60";

/* Form controls */
export const reportInput =
    "w-full h-9 px-3 rounded-lg text-[13px] transition-colors " +
    "bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 " +
    "focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 " +
    "dark:bg-gray-800/80 dark:border-gray-700 dark:text-white dark:placeholder-gray-500";

export const reportLabel =
    "block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1";

export const reportBtnPrimary =
    "inline-flex items-center justify-center h-9 px-4 rounded-lg text-[12.5px] font-semibold text-white transition-all " +
    "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 " +
    "dark:bg-blue-500 dark:hover:bg-blue-600 " +
    "shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)] dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)] " +
    "hover:shadow-[0_6px_20px_-4px_rgba(37,99,235,0.55)]";

export const reportBtnSecondary =
    "inline-flex items-center justify-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg text-[12.5px] font-semibold transition-colors " +
    "bg-white dark:bg-gray-800 " +
    "border border-gray-200 dark:border-gray-700 " +
    "text-gray-700 dark:text-gray-300 " +
    "hover:bg-gray-50 hover:border-gray-300 " +
    "dark:hover:bg-gray-700/70 dark:hover:border-gray-600";

/* Table */
export const reportTableHeader =
    "bg-gray-50/90 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-800";
export const reportTableTh =
    "px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider";
export const reportTableTd =
    "px-4 py-2.5 text-[13px] text-gray-800 dark:text-gray-200 align-middle";
export const reportTableRow =
    "border-b border-gray-100 dark:border-gray-800/80 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors";
export const reportTableThCompact =
    "px-2 py-1.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider";
export const reportTableTdCompact =
    "px-2 py-1.5 text-[13px] text-gray-800 dark:text-gray-200 align-middle";
export const reportTableRowCompact =
    "border-b border-gray-100 dark:border-gray-800/80 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors";

export const reportSortBtn =
    "inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors";

/* Section titles */
export const reportSectionTitle =
    "flex items-center gap-2 px-4 sm:px-5 py-3 rounded-t-2xl border-b " +
    "border-gray-100 bg-gray-50/80 dark:border-gray-800 dark:bg-gray-800/50";

export const reportToolbarRow =
    "flex flex-col gap-3 border-b px-4 sm:px-5 py-3 sm:flex-row sm:items-center sm:justify-between " +
    "border-gray-100 bg-gray-50/60 dark:border-gray-800 dark:bg-gray-800/40";

export const reportDropdownPanel =
    "absolute right-0 z-50 mt-2 max-h-72 w-64 overflow-y-auto rounded-xl border p-3 shadow-xl " +
    "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900";

export const reportAccent = "text-blue-600 dark:text-blue-400";
export const reportAccentGreen = "text-emerald-600 dark:text-emerald-400";

export const reportPaginationWrap =
    `${reportCard} mt-3 py-2 px-4 border-gray-200 dark:border-gray-800`;

export const reportHubSectionHeader =
    "flex w-full items-center justify-between gap-3 border-b px-4 sm:px-5 py-3.5 text-left transition-colors " +
    "border-gray-100 bg-gray-50/70 hover:bg-gray-100/80 " +
    "dark:border-gray-800 dark:bg-gray-800/40 dark:hover:bg-gray-800/70";

export const statIconWrap = (color) => `p-2 rounded-lg shrink-0 ${color}`;

export const STAT_COLORS = [
    "bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400",
    "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400",
    "bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400",
    "bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400",
    "bg-teal-100 text-teal-600 dark:bg-teal-500/20 dark:text-teal-400",
    "bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400",
];

export const STAT_ACCENT_KEYS = ["blue", "emerald", "amber", "red", "blue", "emerald"];

/* Legacy alias */
export const reportHeaderShell = reportShell;
